import React, {useEffect, useState} from 'react';
import {Table, Card, Input, Typography, Tag, Space, Button, Modal, Form, message, AutoComplete, Select} from 'antd';
import {UserOutlined, SearchOutlined, PlusOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {Agent} from "../../types";
import api from '../../api';

const {Title} = Typography;
const {Option} = Select;

const nationalityOptions = ["Japan", "Taiwan", "Hong Kong", "China", "Singapore", "Korea"];
const programOptions = ["Rolling", "Hybrid", "ROL", "PS10", "PS20", "PS30", "PS40", "PS50", "PS60", "PS70", "PS80"];

const AgentsPage: React.FC = () => {
    const [agents, setAgents] = useState<Agent[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [form] = Form.useForm();

    useEffect(() => {
        const fetchAgents = async () => {
            setLoading(true)
            try {
                const response = await api.get('/daia/agents/');
                const agentList = Array.isArray(response.data) ? response.data : response.data.results || [];
                setAgents(agentList);
            } catch (error) {
                console.error('Error fetching agents:', error);
            } finally {
                setLoading(false)
            }
        };

        fetchAgents();
    }, []);

    const handleCreateAgent = async () => {
        try {
            const values = await form.validateFields();
            await api.post('/daia/agents/', values);
            message.success('Agent created successfully');
            setIsModalVisible(false);
            form.resetFields();
            const refreshed = await api.get('/daia/agents/');
            const agentList = Array.isArray(refreshed.data) ? refreshed.data : refreshed.data.results || [];
            setAgents(agentList);
        } catch (error: any) {
            console.error('Error creating agent:', error);
            if (error.response?.data) {
                message.error(`Failed to create agent: ${JSON.stringify(error.response.data)}`);
            } else {
                message.error('Failed to create agent');
            }
        }
    };

    const filteredAgents = agents.filter(agent =>
        `${agent.first_name} ${agent.last_name}`.toLowerCase().includes(searchText.toLowerCase()) ||
        agent.program.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns: ColumnsType<Agent> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            align: 'center',
            render: (id: number) => <Tag color="blue">{id}</Tag>,
        },
        {
            title: 'Full Name',
            key: 'full_name',
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{color: '#1890ff'}}/>
                    <strong>{record.first_name} {record.last_name}</strong>
                </Space>
            ),
        },
        {
            title: 'Nationality',
            dataIndex: 'nationality',
            key: 'nationality',
            align: 'center',
        },
        {
            title: 'Program',
            dataIndex: 'program',
            key: 'program',
            render: (program: string) => <Tag color="purple">{program}</Tag>,
        },
    ];

    return (
        <Card
            title={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}
                >
                    <Title level={2} style={{margin: 0}}>
                        Agents
                    </Title>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={() => setIsModalVisible(true)}>
                        Create Agent
                    </Button>
                </div>
            }
            style={{margin: '16px', padding: '16px'}}
        >
            <Input
                placeholder="Search agents by name or program..."
                prefix={<SearchOutlined/>}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{marginBottom: '16px', width: '100%', maxWidth: 320}}
            />

            <Table
                scroll={{x: 'max-content'}}
                loading={loading}
                dataSource={filteredAgents.map((agent, index) => ({
                    ...agent,
                    key: `agent-${index}`,
                }))}
                columns={columns}
                bordered
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                onRow={() => ({
                    style: {
                        cursor: 'pointer',
                        transition: 'background 0.2s ease-in-out',
                    },
                })}
            />

            <Modal
                title="Create New Agent"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="Create"
            >
                <Form form={form} layout="vertical" onFinish={handleCreateAgent}>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{required: true, message: 'First name is required'}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{required: true, message: 'Last name is required'}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="nationality"
                        label="Nationality"
                        rules={[{required: true, message: 'Nationality is required'}]}
                    >
                        <AutoComplete
                            options={nationalityOptions.map((n) => ({value: n}))}
                            filterOption={(inputValue, option) =>
                                option!.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        >
                            <Input/>
                        </AutoComplete>
                    </Form.Item>
                    <Form.Item
                        name="program"
                        label="Program"
                        rules={[{required: true, message: 'A program selection is required'}]}
                    >
                        <Select placeholder="Select program">
                            {programOptions.map((program) => (
                                <Option key={program} value={program}>
                                    {program}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default AgentsPage;
